import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X as XIcon } from 'lucide-react';
import cn from '../../utils/cn';

/**
 * Sheet component.
 * Root component that manages the sheet state.
 * @param {object} props - All props from SheetPrimitive.Root.
 * @returns {JSX.Element}
 */
function Sheet(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

Sheet.propTypes = {
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
};

Sheet.defaultProps = {
  open: undefined,
  onOpenChange: undefined,
  children: null,
};

/**
 * SheetTrigger component.
 * Component that triggers the sheet to open.
 * @param {object} props - All props from SheetPrimitive.Trigger.
 * @returns {JSX.Element}
 */
function SheetTrigger(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

SheetTrigger.propTypes = {
  asChild: PropTypes.bool,
  children: PropTypes.node,
};

SheetTrigger.defaultProps = {
  asChild: undefined,
  children: null,
};

/**
 * SheetClose component.
 * Component that closes the sheet when clicked.
 * @param {object} props - All props from SheetPrimitive.Close.
 * @returns {JSX.Element}
 */
function SheetClose(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

SheetClose.propTypes = {
  asChild: PropTypes.bool,
  children: PropTypes.node,
};

SheetClose.defaultProps = {
  asChild: undefined,
  children: null,
};

/**
 * SheetPortal component.
 * Portal component for sheet content.
 * @param {object} props - All props from SheetPrimitive.Portal.
 * @returns {JSX.Element}
 */
function SheetPortal(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

SheetPortal.propTypes = {
  children: PropTypes.node,
};

SheetPortal.defaultProps = {
  children: null,
};

/**
 * SheetOverlay component.
 * Overlay component for the sheet.
 * @param {object} props - className, and all props from SheetPrimitive.Overlay.
 * @param {string} [props.className] - Additional CSS classes.
 * @returns {JSX.Element}
 */
const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <SheetPrimitive.Overlay
      ref={ref}
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
});

SheetOverlay.displayName = 'SheetOverlay';

SheetOverlay.propTypes = {
  className: PropTypes.string,
};

SheetOverlay.defaultProps = {
  className: '',
};

/**
 * SheetContent component.
 * Content component for the sheet.
 * @param {object} props - className, children, side, and all props from SheetPrimitive.Content.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {React.ReactNode} props.children - Child components.
 * @param {string} [props.side] - Side of the sheet (top, right, bottom, left).
 * @returns {JSX.Element}
 */
function SheetContent({ className, children, side = 'right', ...props }) {
  const { t } = useTranslation('common');
  const closeLabel = t('buttons.close_button');

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'bg-white data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
          side === 'right' &&
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
          side === 'left' &&
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
          side === 'top' &&
            'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          className,
        )}
        style={{ backdropFilter: 'blur(40px)' }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
        <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
          <XIcon className="h-6 w-6" />
          <span className="sr-only">{closeLabel}</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

SheetContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  side: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
};

SheetContent.defaultProps = {
  className: '',
  side: 'right',
};

/**
 * SheetHeader component.
 * Header component for the sheet.
 * @param {object} props - className, and all props from div.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
function SheetHeader({ className, ...props }) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-1.5 p-4', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

SheetHeader.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

SheetHeader.defaultProps = {
  className: '',
  children: null,
};

/**
 * SheetFooter component.
 * Footer component for the sheet.
 * @param {object} props - className, and all props from div.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
function SheetFooter({ className, ...props }) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

SheetFooter.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

SheetFooter.defaultProps = {
  className: '',
  children: null,
};

/**
 * SheetTitle component.
 * Title component for the sheet.
 * @param {object} props - className, and all props from SheetPrimitive.Title.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
function SheetTitle({ className, ...props }) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-foreground font-semibold', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

SheetTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

SheetTitle.defaultProps = {
  className: '',
  children: null,
};

/**
 * SheetDescription component.
 * Description component for the sheet.
 * @param {object} props - className, and all props from SheetPrimitive.Description.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
function SheetDescription({ className, ...props }) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

SheetDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

SheetDescription.defaultProps = {
  className: '',
  children: null,
};

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
